import { Type } from "@sinclair/typebox";
import { TypeBoxToType } from "@whatslly/types/TypeBoxToType";
import { Direction, Operator, OutboundMessageType } from "./enums";

export const PolicyCondition = Type.Union([
  Type.Object({ type: Type.Literal("user"), userId: Type.Array(Type.String()) }),
  Type.Object({ type: Type.Literal("internal_users"), include: Type.Boolean() }),
  Type.Object({ type: Type.Literal("group"), groupId: Type.Array(Type.String()) }),
  Type.Object({ type: Type.Literal("mimeType"), mimeType: Type.Array(Type.String()) }),
  Type.Object({
    type: Type.Literal("fileSize"),
    maxSize: Type.Optional(Type.Number()),
    size: Type.Optional(Type.Number()),
    operator: Type.Optional(
      Type.Union([Type.Literal(Operator.GT), Type.Literal(Operator.LT), Type.Literal(Operator.GTE), Type.Literal(Operator.LTE)])
    ),
  }),
  Type.Object({
    type: Type.Literal("direction"),
    direction: Type.Literal(Direction.OUTBOUND),
    msgType: Type.Optional(Type.Array(Type.Enum(OutboundMessageType))),
  }),
  Type.Object({
    type: Type.Literal("direction"),
    direction: Type.Union([Type.Literal(Direction.INBOUND), Type.Literal(Direction.UPLOAD), Type.Literal(Direction.DOWNLOAD)]),
  }),
  Type.Object({ type: Type.Literal("template") }),
  Type.Object({ type: Type.Literal("devtools") }),
  Type.Object({ type: Type.Literal("rule"), id: Type.Union([Type.String(), Type.Array(Type.String())]) }),
]);

export type PolicyCondition = TypeBoxToType<typeof PolicyCondition>;

export const MIME_TYPE_OPTIONS = [
  { label: "PDF", value: "^(application/pdf|application/x-pdf)$", extensions: ["pdf"] },
  { label: "Audio", value: "^audio/", extensions: [] },
  { label: "Video", value: "^video/", extensions: [] },
  { label: "Image", value: "^image/", extensions: [] },
  { label: "Powershell (ps1)", value: "^(application/x-powershell|text/x-powershell)$", extensions: ["ps1"] },
  { label: "VBScript (vbs)", value: "^(text/vbscript|application/x-vbs)$", extensions: ["vbs"] },
  {
    label: "Microsoft Executable (exe, bat, com, msi, dll, scr)",
    extensions: ["exe", "bat", "com", "msi", "dll", "scr"],
    value: `^(${[
      "application/vnd.microsoft.portable-executable",
      "application/x-msdownload",
      "application/x-msdos-program",
      "application/x-dosexec",
      "application/x-msi",
      "application/x-bat",
    ].join("|")})$`,
  },
  {
    label: "MacOS Executable (dmg, pkg)",
    extensions: ["dmg", "pkg"],
    value: `^(${["application/vnd.apple.installer+xml", "application/x-apple-diskimage", "application/x-xar"].join("|")})$`,
  },
  { label: "Microsoft Silverlight (scr)", value: "^application/x-silverlight$", extensions: ["scr"] },
  { label: "ACCDB (accdb)", value: "^application/msaccess$", extensions: ["accdb"] },
  { label: "SQL (sql, sqlproj, eql)", value: "^(application/sql|application/x-sql|text/x-sql)$", extensions: ["sql", "sqlproj", "eql"] },
  {
    label: "Appx (appx, msix)",
    value: "^(application/vnd.ms-appx|application/appx|application/msix|application/appinstaller)$",
    extensions: ["appx", "msix"],
  },
  { label: "Bash Script (sh)", value: "^(application/x-sh|application/x-shellscript|text/x-sh|text/x-shellscript)$", extensions: ["sh"] },
  { label: "Msc (msc)", value: "^application/x-msc$", extensions: ["msc"] },
  {
    label: "Python (py, p, pkl, pickle, pyd, pyw)",
    value: "^(application/x-python-code|application/x-python|text/x-python|text/x-script.python)$",
    extensions: ["py", "p", "pkl", "pickle", "pyd", "pyw"],
  },
  { label: "Window Meta (wmf)", value: "^(application/x-msmetafile|image/x-wmf|image/wmf)$", extensions: ["wmf"] },
  { label: "Microsoft Access Database (mdb)", value: "^application/x-msaccess$", extensions: ["mdb"] },
  {
    label: "Microsoft Excel (xls, xlsx, xlsm, xlam, xlsb, xlb, slk, xltm, xltx)",
    extensions: ["xls", "xlsx", "xlsm", "xlam", "xlsb", "xlb", "slk", "xltm", "xltx"],
    value: `^(${[
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.template",
      "application/vnd.ms-excel.sheet.macroEnabled.12",
      "application/vnd.ms-excel.addin.macroEnabled.12",
      "application/vnd.ms-excel.sheet.binary.macroEnabled.12",
      "application/vnd.ms-excel.template.macroEnabled.12",
      "application/x-excel",
      "application/x-msexcel",
    ].join("|")})$`,
  },
  { label: "Microsoft Outlook Message (msg)", value: "^application/vnd.ms-outlook$", extensions: ["msg"] },
  {
    label: "Microsoft PowerPoint (ppt, pptx, pptm, potx, ppsx, ppam, potm, ppsm)",
    extensions: ["ppt", "pptx", "pptm", "potx", "ppsx", "ppam", "potm", "ppsm"],
    value: `^(${[
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/vnd.ms-powerpoint.presentation.macroEnabled.12",
      "application/vnd.openxmlformats-officedocument.presentationml.template",
      "application/vnd.openxmlformats-officedocument.presentationml.slideshow",
      "application/vnd.ms-powerpoint.addin.macroEnabled.12",
      "application/vnd.ms-powerpoint.template.macroEnabled.12",
      "application/vnd.ms-powerpoint.slideshow.macroEnabled.12",
      "application/x-mspowerpoint",
    ].join("|")})$`,
  },
  { label: "Microsoft Rich Text Format (rtf)", value: "^(application/rtf|text/rtf)$", extensions: ["rtf"] },
  {
    label: "Microsoft Visio (vsd, vsdx, vss, vst, vdx, vsx, vtx, vssx, vstx, vsdm, vssm, vstm, vsw, vsl)",
    extensions: ["vsd", "vsdx", "vss", "vst", "vdx", "vsx", "vtx", "vssx", "vstx", "vsdm", "vssm", "vstm", "vsw", "vsl"],
    value: `^(${[
      "application/vnd.visio",
      "application/vnd.ms-visio.drawing",
      "application/vnd.ms-visio.template",
      "application/vnd.ms-visio.stencil",
    ].join("|")})$`,
  },
  {
    label: "Microsoft Word (doc, docx, docm, dotx, dotm)",
    extensions: ["doc", "docx", "docm", "dotx", "dotm"],
    value: `^(${[
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-word.document.macroEnabled.12",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.template",
      "application/vnd.ms-word.template.macroEnabled.12",
      "application/vnd.ms-word",
      "application/x-msword",
    ].join("|")})$`,
  },
  {
    label: "Apple Documents (keynote, numbers, pages)",
    extensions: ["key", "keynote", "numbers", "pages"],
    value: `^(${[
      "application/vnd.apple.pages",
      "application/vnd.apple.numbers",
      "application/vnd.apple.keynote",
      "application/x-iwork-pages-sffpages",
      "application/x-iwork-numbers-sffnumbers",
      "application/x-iwork-keynote-sffkey",
    ].join("|")})$`,
  },
  { label: "CSV", value: "^(application|text)/csv$", extensions: ["csv"] },
  { label: "DAT files (dat)", value: "^application/dat$", extensions: ["dat"] },
  { label: "Text (txt)", value: "^text/plain$", extensions: ["txt"] },
  { label: "TeX/LaTeX (tex)", value: "^(application/x-tex|application/x-latex|text/x-tex)$", extensions: ["tex"] },
  {
    label: "C/C++ (c, h, cc, hh, cpp, hpp)",
    value: "^(application/x-c|text/x-c|text/x-csrc|text/x-chdr)$",
    extensions: ["c", "h", "cc", "hh", "cpp", "hpp"],
  },
  { label: "C# (cs)", value: "^(application/x-csharp|text/x-csharp)$", extensions: ["cs"] },
  { label: "F Sharp (fs, fsproj, fsscript, fsx)", value: "^(application/x-fsharp|text/x-fsharp)$", extensions: ["fs", "fsproj", "fsscript", "fsx"] },
  { label: "Include (inc)", value: "^(application/x-include|text/x-include)$", extensions: ["inc"] },
  { label: "Java (java, jav, j, sjava)", value: "^(application/x-java|text/x-java|text/x-java-source)$", extensions: ["java", "jav", "j", "sjava"] },
  { label: "Ruby (rb)", value: "^(application/x-ruby|text/x-ruby)$", extensions: ["rb"] },
  { label: "SAS (sas)", value: "^(application/x-sas|application/x-sas-syntax|application/x-sas-data)$", extensions: ["sas"] },
  { label: "SC (sc)", value: "^application/x-sc$", extensions: ["sc"] },
  { label: "Scala (scala)", value: "^(application/x-scala|text/x-scala)$", extensions: ["scala"] },
  { label: "Windows Script Component (sct)", value: "^text/scriptlet$", extensions: ["sct"] },
  { label: "X1B (x1b)", value: "^application/x-x1b$", extensions: ["x1b"] },
  { label: "XAML (xaml)", value: "^application/xaml(\\+xml)?$", extensions: ["xaml"] },
  { label: "YAML (yaml, yml)", value: "^(application/yaml|application/x-yaml|text/yaml|text/x-yaml)$", extensions: ["yaml", "yml"] },
  {
    label: "Java Applet (jar, class, applet)",
    value: "^(application/java-archive|application/x-java-applet|application/java-vm)$",
    extensions: ["jar", "class", "applet"],
  },
  {
    label: "JavaScript",
    value: "^(application/javascript|text/javascript|application/x-javascript|application/ecmascript)$",
    extensions: ["js", "mjs", "cjs"],
  },
  {
    label: "Compressed (zip, gz, tar, 7z, rar, bz2, xz, lzma, cab, z, sit, ace)",
    extensions: ["zip", "gz", "tar", "7z", "rar", "bz2", "xz", "lzma", "cab", "z", "sit", "ace"],
    value: `^(${[
      "application/zip",
      "application/gzip",
      "application/x-tar",
      "application/x-7z-compressed",
      "application/vnd.rar",
      "application/x-rar-compressed",
      "application/x-rar",
      "application/x-compressed",
      "application/x-zip-compressed",
      "application/x-bzip",
      "application/x-bzip2",
      "application/x-xz",
      "application/x-lzma",
      "application/vnd.ms-cab-compressed",
      "application/x-compress",
      "application/x-stuffit",
      "application/x-ace-compressed",
    ].join("|")})$`,
  },
  { label: "Windows Shell Link file (lnk)", value: "^application/x-ms-shortcut$", extensions: ["lnk"] },
  { label: "Microsoft HTML Application (hta)", value: "^(application/hta|application/mshta)$", extensions: ["hta"] },
] as const;
